import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { User, UserResponse } from '../../../core/models/user.model';
import { InviteUserModel } from '../../../core/models/invite-user.model';
import {
    UsageReportDto,
    AccountSettingsDto,
    DomainTopicsDto,
    IntentTrialVerifySelectedFiltersPayloadDto,
    IntentTrialIndustriesResponseDto,
    IntentTrialSubmitPayloadDto,
    IntentTrialSubmitResponseDto
} from '../../dto';
import { BaseApiService } from './base-api.service';
import { HttpHeaders, HttpParams } from '@angular/common/http';
import { FavoritePageEnum } from '../../enums';

@Injectable({
    providedIn: 'root',
})
export class AccountApiService {
    constructor(private baseApiService: BaseApiService) { }

    invite(data: InviteUserModel): Observable<any> {
        return this.baseApiService.post('account/invite', data, { showError: true });
    }

    getUsers(): Observable<Array<User>> {
        return this.baseApiService.get('account/users', { showError: true }).pipe(map((response: UserResponse) => response.users));
    }

    getAccountActiveUsers(accountId: number): Observable<UserResponse> {
        return this.baseApiService.get<UserResponse>(`account/activeUsers/${accountId}`, { excludeImpersonate: true });
    }

    deleteUser(userId: number) {
        return this.baseApiService.delete(`account/user/${userId}`, { showError: true });
    }

    resendInvite(email: string) {
        return this.baseApiService.post(`account/resendInvite?email=${email}`, null, { showError: true });
    }

    acceptInvite(data) {
        return this.baseApiService.post('account/acceptInvite', data);
    }

    restore(userid: number) {
        return this.baseApiService.post(`account/restore/${userid}`, null, { showError: true });
    }

    usageReport(reportDetailsObj: UsageReportDto): Observable<any> {
        return this.baseApiService.post<any>('account/usageReport', reportDetailsObj, { excludeImpersonate: true, showError: true }, { responseType: 'text' });
    }

    getUsersCreditLimit() {
        return this.baseApiService.get('usercredits', { showError: true });
    }

    setUsersCreditLimit(data: any): Observable<{ response: string }> {
        return this.baseApiService.post('usercredits', data, { showError: true, showSuccess: true });
    }

    getSettings(accountId: number): Observable<AccountSettingsDto> {
        return this.baseApiService.get(`account/settings/${accountId}`);
    }

    forgotPassword(email: string): Observable<any> {
        return this.baseApiService.post('account/forgotPassword', {}, { params: { email, property: 'prospector' } });
    }

    resetPassword(params): Observable<any> {
        return this.baseApiService.post('account/resetPassword', {}, { params });
    };

    whoami(additionalHeaders?: HttpHeaders): Observable<User> {
        return this.baseApiService.get('account/whoami', { params: { from: 'portal' }, additionalHeaders});
    }

    validateEmailToken(params): Observable<any> {
        return this.baseApiService.post('account/validateToken', {}, { params });
    }

    registrationConfirm(params): Observable<any> {
        return this.baseApiService.get('account/registrationConfirm', { params });
    }

    resendConfirmationEmail(params): Observable<any> {
        return this.baseApiService.get('account/resendconfirmationemail', { params });
    }

    setFavoritePage(userId: number, data:{favoritePage: FavoritePageEnum} ): Observable<any> {
        return this.baseApiService.post(`user/meta/${userId}`, data);
    }

    getIntentTrialSignupDomainTopics(domain: string): Observable<DomainTopicsDto> {
        const params = new HttpParams().set('domain', domain);
        return this.baseApiService.get<DomainTopicsDto>('intenttrial/domain-topics', { params });
    }

    getIntentTrialIndustries(): Observable<IntentTrialIndustriesResponseDto> {
        return this.baseApiService.get<IntentTrialIndustriesResponseDto>('intenttrial/industries');
    }

    verifyIntentTrialSelectedFilters(payload: IntentTrialVerifySelectedFiltersPayloadDto): Observable<boolean> {
        return this.baseApiService.post<boolean>('intenttrial/verify-selected-filters', payload, { showError: true });
    }

    postIntentTrial(payload: IntentTrialSubmitPayloadDto): Observable<IntentTrialSubmitResponseDto> {
        return this.baseApiService.post<IntentTrialSubmitResponseDto>('intenttrial', payload, { showError: false });
    }

}
